import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Box,
  Divider,
  Avatar,
  Chip,
} from "@mui/material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth0 } from "../hooks/useAuth0";
import { useRole } from "../hooks/useRole";
import patientService from "../services/patientService";
import api from "../services/api";
import SidePanel from "./SidePanel";


// ============= ICONS =============
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import HomeIcon from "@mui/icons-material/Home";
import DashboardIcon from "@mui/icons-material/Dashboard";
import LogoutIcon from "@mui/icons-material/Logout";
import InfoIcon from "@mui/icons-material/Info";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import LoginIcon from "@mui/icons-material/Login";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PersonIcon from "@mui/icons-material/Person";
import TimelineIcon from "@mui/icons-material/Timeline";

// Education Icons
import SchoolIcon from "@mui/icons-material/School";
import FavoriteIcon from "@mui/icons-material/Favorite";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import AccessibilityIcon from "@mui/icons-material/Accessibility";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";

// Clinical Trials Icons
import SearchIcon from "@mui/icons-material/Search";
import ListIcon from "@mui/icons-material/List";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import AddIcon from "@mui/icons-material/Add";

// Patient Management Icons
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import LayersIcon from "@mui/icons-material/Layers";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import EventNoteIcon from "@mui/icons-material/EventNote";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";   
import MedicationIcon from "@mui/icons-material/Medication";


// Tools Icons
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import FlightIcon from "@mui/icons-material/Flight";
import FolderIcon from "@mui/icons-material/Folder";
import BiotechIcon from "@mui/icons-material/Biotech";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import FindInPageIcon from "@mui/icons-material/FindInPage";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import CalculateIcon from "@mui/icons-material/Calculate";
import InsightsIcon from "@mui/icons-material/Insights"; 

// Resources Icons
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import ArticleIcon from "@mui/icons-material/Article";
import BookIcon from "@mui/icons-material/Book";
import BusinessIcon from "@mui/icons-material/Business";

// Admin Icons
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import SettingsIcon from "@mui/icons-material/Settings";
import PeopleIcon from "@mui/icons-material/People";
import SecurityIcon from "@mui/icons-material/Security";

import ContactDialog from "./ContactDialog";
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';

// ============= BRAND COLORS =============
const PRIMARY = "#1A5EA8";
const SECONDARY = "#0B2545";
const GRADIENT = `linear-gradient(135deg, ${PRIMARY} 0%, ${SECONDARY} 100%)`;
const PANEL_ACCENT = "rgba(102, 126, 234, 0.08)";

// ============= MENU DEFINITIONS =============

const educationItems = [
  {
    label: "CKD Basics",
    path: "/education",
    icon: <SchoolIcon />,
    tier: "free",
    description: "Introduction to chronic kidney disease — what it is and how it works",
  },
  {
    label: "CKD Complications",
    path: "/education/basics",
    icon: <LocalHospitalIcon />,
    tier: "free",
    description: "Cardiovascular, bone & mineral, anemia, and other complications",
  },
  {
    label: "CKD Causes",
    path: "/education/types",
    icon: <BiotechIcon />,
    tier: "free",
    description: "Diabetic, hypertensive, glomerulonephritis, PKD, and other causes",
    // ← no path — has children instead
    children: [
      {
        label: "Diabetic Nephropathy",
        path: "/education/types/diabetic",
        icon: <SchoolIcon />,
        tier: "free",
        description: "Most common cause — diabetes-related kidney damage",
      },
      {
        label: "Hypertensive Nephrosclerosis",
        path: "/education/types/hypertensive",
        icon: <FavoriteIcon />,
        tier: "free",
        description: "Second most common — blood-pressure-related damage",
      },
      {
        label: "Glomerulonephritis",
        path: "/education/types/glomerulonephritis",
        icon: <LocalHospitalIcon />,
        tier: "free",
        description: "Immune-mediated — IgA nephropathy, FSGS, lupus nephritis",
      },
      {
        label: "Polycystic Kidney Disease",
        path: "/education/types/pkd",
        icon: <BiotechIcon />,
        tier: "free",
        description: "Hereditary — cysts gradually enlarge the kidneys",
      },
      {
        label: "Other & Unknown Causes",
        path: "/education/types/other",
        icon: <VisibilityIcon />,
        tier: "free",
        description: "Obstruction, infection, medications, or no clear cause",
      },
    ],
  },
  {
    label: "Treatment Guide",
    path: "/education/treatment",
    icon: <MedicationIcon />,
    tier: "free",
    description: "Kidney-protective medications by CKD cause — ACE/ARB, SGLT2i, finerenone and more",
  },
  {
    label: "CKD Staging Calculator",
    path: "/education/calculator",
    icon: <CalculateIcon />,
    tier: "free",
    description: "Calculate your KDIGO stage and risk from eGFR and UACR",
  },
  {
    label: "Lab Results Guide",
    path: "/learn/labs",
    icon: <BiotechIcon />,
    tier: "free",
    description: "Understanding your CKD lab values",
  },
  {
    label: "Living with CKD",
    path: "/learn/living-with-ckd",
    icon: <AccessibilityIcon />,
    tier: "free",
    description: "Lifestyle, caregiving, and mental health",
  },
];

const clinicalTrialsItems = [
  {
    label: "Search Trials",
    path: "/trials/search",
    icon: <SearchIcon />,
    tier: "free",
    description: "Search ClinicalTrials.gov for CKD studies",
  },
  { 
    label: "Find My Trial", 
    path: "/trials/nlp-search", 
    icon: <AutoAwesomeIcon  />,
    tier: "free",
    description: "Describe your situation — AI finds your match" 
  },
  {
    label: "Browse All Trials",
    path: "/trials/browse",
    icon: <ListIcon />,
    tier: "free",
    description: "Browse all CKD trials in our database",
  },
  "divider",
  {
    label: "My Matched Trials",
    path: "/trials/matched",
    icon: <BookmarkIcon />,
    description: "View your personalized trial matches",
  },
];

const myHealthItems = [
  {
    label: "My Dashboard",
    path: "/dashboard",
    icon: <DashboardIcon />,
    tier: "free",
    description: "Your personalized CKD health overview",
  },
  {
    label: "My Profile",
    path: "/profile",
    icon: <PersonIcon />,
    tier: "free",
    description: "Update your health details and preferences",
  },
  { label: "Find Provider Near Me", 
    path: "/my-health/find-provider", 
    icon: <LocalHospitalIcon /> ,
    tier: "free",
    description: "View your treatment pathway",
  },
  {
    label: "My Treatment Pathway",
    path: "/my-health/pathway",
    icon: <TimelineIcon />,
    tier: "free",
    description: "View your treatment pathway",
  },
  "divider",
    {
    label: "My Care Plan",
    path: "/patients/:patientId/my-care-plan",
    icon: <AssignmentTurnedInIcon />,
    description: "Your goals, tasks, and what to log",
  },
  {
    label: "My Symptoms & Vitals",
    path: "/patients/:patientId/daily-intake",
    icon: <EventNoteIcon />,
    description: "Log today's symptoms, vitals, and medication check-in",
  },
  {
    label: "My Health Log",
    path: "/patients/:patientId/daily-intake/history",
    icon: <MedicationIcon />,
    description: "View your symptoms, vitals, and medication log — print a doctor visit report",
  },
  {
    label: "Find Matching Trials",
    path: "/patients/:patientId/match-trials",
    icon: <LocalHospitalIcon />,
    description: "AI-powered trial matching",
  },
  {
    label: "My Documents",
    path: "/tools/documents",
    icon: <FolderIcon />,
    description: "Medical records and documents",
  },
];

const toolsItems = [
  {
    label: "Trial Comparison",
    path: "/tools/compare-trials",
    icon: <CompareArrowsIcon />,
    description: "Compare up to 3 trials side-by-side",
  },
  {
    label: "Eligibility Checker",
    path: "/tools/eligibility",
    icon: <FactCheckIcon />,
    description: "Quick pre-screening for trial eligibility",
  },
  {
    label: "Clinical Trial Glossary",
    path: "/tools/glossary",
    icon: <MenuBookIcon />,
    tier: "free",
    description: "Understand medical terminology",
  },
  "divider",
  {
    label: "Travel Planner",
    path: "/tools/travel",
    icon: <FlightIcon />,
    description: "Book flights and hotels to trial sites",
  },
  {
    label: "Document Manager",
    path: "/tools/documents",
    icon: <FolderIcon />,
    description: "Store and organize medical records",
  },
  {
    label: "Medication Tracker",
    path: "/tools/medications",
    icon: <MedicationIcon />,
    description: "Track medications and interactions",
  },
  "divider",
  {
    label: "Lab Results Tracker",
    path: "/tools/lab-tracker",
    icon: <BiotechIcon />,
    description: "Monitor lab values over time",
  },
  {
    label: "Trial Calendar",
    path: "/tools/calendar",
    icon: <CalendarMonthIcon />,
    description: "Manage trial appointments",
  },
  {
    label: "Cost Calculator",
    path: "/tools/cost-calculator",
    icon: <CalculateIcon />,
    description: "Estimate trial participation costs",
  },
  {
    label: "Second Opinion Finder",
    path: "/tools/second-opinion",
    icon: <FindInPageIcon />,
    description: "Find nephrology specialists near you",
  },
];



const resourcesItems = [
  {
    label: "Research Library",
    path: "/resources/library",
    icon: <LibraryBooksIcon />,
    tier: "free",
    description: "Curated CKD research and studies",
  },
  {
    label: "Treatment Guidelines",
    path: "/resources/guidelines",
    icon: <ArticleIcon />,
    tier: "free",
    description: "Evidence-based clinical guidelines",
  },
  {
    label: "Clinical Calculators",
    path: "/resources/calculators",
    icon: <CalculateIcon />,
    tier: "free",
    description: "Medical calculators and risk scores",
  },
  "divider",
  {
    label: "Find a Specialist",
    path: "/my-health/find-provider",
    icon: <PersonSearchIcon />,
    tier: "free",
    description: "Search nephrology specialists near you",
  },
  {
    label: "Support Organizations",
    path: "/resources/organizations",
    icon: <BusinessIcon />,
    tier: "free",
    description: "National Kidney Foundation and patient advocacy groups",
  },
  {
  label: "Patient Guides",
  path: "/resources/guides",
  icon: <MenuBookIcon />,
  tier: "free",
  description: "Free downloadable PDF guides for diabetic, hypertensive, and glomerular CKD",
},
];

const adminItems = [
  {
    label: "Import Trials",
    path: "/admin/trials/import",
    icon: <CloudDownloadIcon />,
    roles: ["Admin", "Researcher"],
    description: "Import trials from ClinicalTrials.gov",
  },
  {
    label: "Create New Trial",
    path: "/admin/trials/new",
    icon: <AddIcon />,
    roles: ["Admin", "Provider"],
    description: "Add a new clinical trial to the database",
  },
  {
    label: "Edit Trial",
    path: "/admin/trials/edit/:id",
    icon: <AdminPanelSettingsIcon />,
    roles: ["Admin", "Provider"],
    description: "Edit and manage existing trials",
  },
  "divider",
  {
    label: "User Management",
    path: "/admin/users",
    icon: <PeopleIcon />,
    roles: ["Admin"],
    description: "Manage user accounts and roles",
  },
  {
    label: "Provider & Researcher Approvals",
    path: "/admin/user-verification",
    icon: <FactCheckIcon />,
    roles: ["Admin"],
    description: "Approve pending signups and practice changes, and assign providers to the practices where they treat patients",
  },
  {
    label: "Manage Practices & Providers",
    path: "/admin/manage-practices",
    icon: <SecurityIcon />,
    roles: ["Admin"],
    description: "Add or remove practices, assign providers to the practices where they treat patients, block or unblock providers",
  },
  {
    label: "System Settings",
    path: "/admin/settings",
    icon: <SettingsIcon />,
    roles: ["Admin"],
    description: "Configure system settings",
  },
  {
    label: "Security & Permissions",
    path: "/admin/security",
    icon: <SecurityIcon />,
    roles: ["Admin"],
    description: "Manage permissions and security",
  },
  {
    label: "Translate Trials",
    path: "/admin/translations",
    icon: <SecurityIcon />,
    roles: ["Admin"],
    description: "Manage Trials Translation",
  },
  { label: "Provider API Usage", 
    path: "/admin/api-usage", 
    icon: <InsightsIcon /> ,
    roles: ["Admin"],
    description: "Provider API Usage Report",},
    "divider",
    { label: "Provider Management", 
      path: "/admin/provider/manage" ,
      icon: <SecurityIcon /> ,
      roles: ["Admin"],
      description: "Manage CKD Providers",},
         
];

const patientsItems = [
  {
    label: "Patient List",
    path: "/patients",
    icon: <PeopleAltIcon />,
    roles: ["Provider", "Admin"],
    description: "View all patients",
  },
  {
    label: "Register New Patient",
    path: "/patients/new",
    icon: <PersonAddIcon />,
    roles: ["Provider", "Admin"],
    description: "Create new patient profile",
  },
  "divider",
  {
    label: "Match Patient to Trials",
    path: "/patients/:patientId/match-trials",
    icon: <PersonSearchIcon />,
    roles: ["Provider", "Admin"],
    description: "Find matching clinical trials",
  },
  {
    label: "Patient Daily Intake",
    path: "/patients/:patientId/daily-intake",
    icon: <EventNoteIcon />,
    roles: ["Provider", "Admin"],
    description: "View patient daily logs",
  },
  "divider",
  {
    label: "Batch Matching",
    path: "/patients/batch-match",
    icon: <LayersIcon />,
    roles: ["Provider", "Admin"],
    description: "Match multiple patients at once",
  },
  {
    label: "Dashboard",
    path: "/patients/analytics",
    icon: <AnalyticsIcon />,
    roles: ["Provider", "Admin"],
    description: "Patient KPIs, attention queue, and CKD stage breakdown",
  },
];




// ============= HEADER COMPONENT =============

export default function Header({ onPanelChange }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const navigate = useNavigate();
  const { user, logout, loginWithRedirect } = useAuth0();
  const { roles, loading: rolesLoading } = useRole();

  const [activeMenu, setActiveMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [practiceInfo, setPracticeInfo] = useState(null); // { organizationName, affiliatedPractices }
  const [practiceMenuOpen, setPracticeMenuOpen] = useState(false);
  const [switchingPractice, setSwitchingPractice] = useState(false);
  const isNative = Capacitor.isNativePlatform();
  const nativeLogin = async () => {
  await loginWithRedirect({
    openUrl: async (url) => {
      await Browser.open({ url, windowName: '_self' });
    },
  });
};

const nativeLogout = async () => {
  await logout({
    logoutParams: { returnTo: 'com.verainsight.nephrotrax://callback' },
    openUrl: async (url) => {
      await Browser.open({ url, windowName: '_self' });
    },
  });
};
  const [resolvedPatientId, setResolvedPatientId] = useState(null);

  useEffect(() => {
    if (!user) {
      setResolvedPatientId(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const result = await patientService.getCurrentUserPatient();
      if (!cancelled && result.success && result.data?.id) {
        setResolvedPatientId(result.data.id);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const hasRole = (requiredRole) => {
    if (rolesLoading) return false;
    return roles.includes(requiredRole);
  };

  // Practice name shown under the provider's name, plus the affiliated
  // practices list that powers the switcher — only relevant for
  // Provider accounts, so this stays a no-op otherwise.
  useEffect(() => {
    if (!user || rolesLoading || !hasRole("Provider")) {
      setPracticeInfo(null);
      return;
    }
    let cancelled = false;
    api.get("/v1/provider-account/me")
      .then((res) => {
        if (!cancelled) setPracticeInfo(res.data);
      })
      .catch((err) => {
        console.error("Failed to load practice info:", err);
      });
    return () => {
      cancelled = true;
    };
  }, [user, rolesLoading, roles]);

  const handleSwitchPractice = async (organizationId) => {
    setSwitchingPractice(true);
    try {
      await api.post("/v1/provider-account/me/switch-practice", { organizationId });
      // Full reload rather than just re-fetching /me — every page that
      // scopes data to the provider's active organization (dashboard,
      // patient lists, etc.) needs a fresh load under the new practice
      // context, not just this header's own state.
      window.location.reload();
    } catch (err) {
      console.error("Failed to switch practice:", err);
      setSwitchingPractice(false);
    }
    setPracticeMenuOpen(false);
  };

  // Resolves to the real numeric patient ID once available (the common
  // case — menu navigation gets a real ID directly, no extra resolution
  // needed downstream). Falls back to the literal "me" placeholder during
  // the brief window before resolution completes, or if it fails for any
  // reason — useResolvedPatientId() on the destination page handles that
  // case correctly too, so this is a performance optimization on top of
  // an already-robust fallback, not a replacement for it.
  const getPatientId = () => resolvedPatientId || "me";

  const resolvePatientPath = (path) =>
    path.replace(":patientId", getPatientId());

  const getRoleColor = (role) => {
    switch (role) {
      case "Admin": return "error";
      case "Provider": return "success";
      case "Researcher": return "info";
      case "Patient": return "primary";
      default: return "default";
    }
  };

  const handleMenuToggle = (menuName) => {
    const newMenu = activeMenu === menuName ? null : menuName;
    setActiveMenu(newMenu);
    if (typeof onPanelChange === "function") {
      onPanelChange(newMenu !== null);
    }
  };

  const handleNavigation = (path, state) => {
    const resolved = path.includes(":patientId") ? resolvePatientPath(path) : path;
    navigate(resolved, state ? { state } : undefined);
    setMobileOpen(false);
  };

  // const handleLogout = () => {
  //   setUserMenuOpen(false);
  //   logout({ logoutParams: { returnTo: window.location.origin } });
  // };

  const handleLogout = () => {
  setUserMenuOpen(false);
  if (isNative) {
    nativeLogout();
  } else {
    logout({ logoutParams: { returnTo: window.location.origin } });
  }
};

  const getActiveItems = () => {
    switch (activeMenu) {
      case "learn": return educationItems;
      case "trials": return clinicalTrialsItems;
      case "my health": return myHealthItems;
      case "tools": return toolsItems;
      case "resources": return resourcesItems;
      case "admin": return adminItems;
      case "patients": return patientsItems;
      default: return [];
    }
  };

const menuButtons = [
  { label: "Learn", key: "learn", always: true },
  { label: "Trials", key: "trials", always: true },
  { label: "Health", key: "my health", show: user && hasRole("Patient") },
  { label: "Patients", key: "patients", show: user && (hasRole("Provider") || hasRole("Admin")) },
  { label: "Admin", key: "admin", show: user && (hasRole("Admin") || hasRole("Researcher")) },
  { label: "Tools", key: "tools", always: true },
  { label: "Library", key: "resources", always: true },
];
  // ============= MOBILE DRAWER =============
  const drawer = (
    <Box sx={{ width: 280, height: "100%" }}>
      <Box sx={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        background: GRADIENT, color: "white", p: 0,
      }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box sx={{ height: 64, width: 64, display: "flex", alignItems: "center", justifyContent: "center", p: 1.5 }}>
            <Box component="img" src="/nephrotrax-logo-white.svg" alt="NephroTrax"
              sx={{ height: "100%", width: "100%", objectFit: "contain" }} />
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", ml: 1.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>NephroTrax</Typography>
            <Typography variant="caption" sx={{ fontStyle: "italic", fontSize: "0.7rem", opacity: 0.9 }}>
              Where Insight Meets Care
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={() => setMobileOpen(false)} sx={{ color: "white", mr: 1 }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {user ? (
        <Box sx={{ p: 2, bgcolor: PANEL_ACCENT, display: "flex", alignItems: "center", gap: 2 }}>
          <Avatar src={user?.picture} alt={user?.name} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" fontWeight={600}>{user?.name}</Typography>
            <Typography variant="caption" color="text.secondary">{user?.email}</Typography>
            {!rolesLoading && roles.length > 0 && (
              <Box sx={{ mt: 0.5 }}>
                <Chip label={roles[0]} size="small" color={getRoleColor(roles[0])}
                  sx={{ fontSize: "0.7rem", height: 20 }} />
              </Box>
            )}
          </Box>
        </Box>
      ) : (
        <Box sx={{ p: 2, bgcolor: PANEL_ACCENT, display: "flex", flexDirection: "column", gap: 1 }}>
          <Button fullWidth variant="contained"
            onClick={() => { navigate("/signup"); setMobileOpen(false); }}
            sx={{ bgcolor: PRIMARY, "&:hover": { bgcolor: SECONDARY } }}>
            Sign Up
          </Button>
          <Button fullWidth variant="outlined"
            onClick={() => { 
              const platform = Capacitor.getPlatform();
              if (platform === 'ios' || platform === 'android' || isNative) {
              nativeLogin();
          } else {
            loginWithRedirect();
            }
               setMobileOpen(false);  
            }}
            sx={{ color: PRIMARY, borderColor: PRIMARY }}>
            Login
          </Button>
        </Box>
      )}

      <Divider />

      <List sx={{ overflowY: "auto" }}>
        <ListItemButton onClick={() => handleNavigation("/")}>
          <ListItemIcon sx={{ color: PRIMARY }}><HomeIcon /></ListItemIcon>
          <ListItemText primary="Home" />
        </ListItemButton>
        <ListItemButton onClick={() => handleNavigation("/about")}>
    <ListItemIcon sx={{ color: PRIMARY }}><InfoIcon /></ListItemIcon>
    <ListItemText primary="About" />
  </ListItemButton>
        {user && (
          <ListItemButton onClick={() => handleNavigation("/dashboard")}>
            <ListItemIcon sx={{ color: PRIMARY }}><DashboardIcon /></ListItemIcon>
            <ListItemText primary="Dashboard" />
          </ListItemButton>
        )}
      </List>

      {[
        { label: "Learn", items: educationItems },
        { label: "Trials", items: clinicalTrialsItems },
        ...(user ? [{ label: "Health", items: myHealthItems }] : []),
        ...(user && (hasRole("Provider") || hasRole("Admin")) ? [{ label: "Patients", items: patientsItems }] : []),
        ...(user && (hasRole("Admin") || hasRole("Researcher")) ? [{ label: "Admin", items: adminItems }] : []),
        { label: "Tools", items: toolsItems },
        { label: "Resources", items: resourcesItems },
      ].map(({ label, items }) => (
        <Box key={label}>
          <Divider />
          <Box sx={{ px: 2, py: 1, bgcolor: "rgba(0,0,0,0.02)" }}>
            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, textTransform: "uppercase", letterSpacing: 1 }}>
              {label}
            </Typography>
          </Box>
          <List dense>
            {items.filter(i => i !== "divider").map((item, idx) => (
              <ListItem key={idx} button onClick={() => handleNavigation(item.path)}
                sx={{ "&:hover": { bgcolor: PANEL_ACCENT } }}>
                <ListItemIcon sx={{ color: PRIMARY, minWidth: 36 }}>{item.icon}</ListItemIcon>
                <ListItemText
                  primary={item.label}
                  secondary={item.description}
                  primaryTypographyProps={{ fontSize: "0.9rem", fontWeight: 500 }}
                  secondaryTypographyProps={{ fontSize: "0.75rem", noWrap: true }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      ))}

      {user && (
        <>
          <Divider />
          <List>
            <ListItemButton onClick={handleLogout}>
              <ListItemIcon sx={{ color: "#ff5252", minWidth: 36 }}><LogoutIcon /></ListItemIcon>
              <ListItemText primary="Logout" primaryTypographyProps={{ color: "#ff5252" }} />
            </ListItemButton>
          </List>
        </>
      )}
    </Box>
  );

  // ============= DESKTOP HEADER =============
  return (
    <>
      <AppBar position="fixed" elevation={2}
        sx={{ 
    zIndex: (theme) => theme.zIndex.drawer + 1, 
    background: GRADIENT,
    paddingTop: 'env(safe-area-inset-top)'
  }}>
        <Toolbar>
          {isMobile && (
  <IconButton 
    color="inherit" 
    edge="start" 
    onClick={() => setMobileOpen(true)} 
    sx={{ mr: 2 }}
  >
    <MenuIcon />
  </IconButton>
)}

          {/* Logo */}
          <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
            <Box component={RouterLink} to="/"
              sx={{ height: 64, width: 64, display: "flex", alignItems: "center", justifyContent: "center", p: 1,
                transition: "all 0.3s ease", "&:hover": { transform: "scale(1.1)" } }}>
              <Box component="img" src="/nephrotrax-logo-white.svg" alt="NephroTrax"
                sx={{ height: "100%", width: "100%", objectFit: "contain" }} />
            </Box>
            <Box component={RouterLink} to="/"
              sx={{ color: "white", textDecoration: "none", display: "flex", flexDirection: "column" }}>
              <Typography variant="h6" noWrap
                sx={{ fontWeight: 700, fontSize: { xs: "1.1rem", md: "1.3rem" }, letterSpacing: 0.5, lineHeight: 1.2 }}>
                NephroTrax
              </Typography>
              <Typography variant="caption"
                sx={{ fontStyle: "italic", fontSize: { xs: "0.65rem", md: "0.75rem" }, opacity: 0.9, mt: -0.5 }}>
                Where Insight Meets Care
              </Typography>
            </Box>
          </Box>

          {/* Desktop Menu Buttons */}
          {!isMobile && (
            <>
              <Button color="inherit" component={RouterLink} to="/" sx={{ fontWeight: 500 }}>
                Home
              </Button>

              {menuButtons.map(({ label, key, always, show }) => {
                if (!always && !show) return null;
                const isActive = activeMenu === key;
                return (
                  <Button
                    key={key}
                    color="inherit"
                    onClick={() => handleMenuToggle(key)}
                    endIcon={<ArrowDropDownIcon sx={{
                      transform: isActive ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.2s ease",
                    }} />}
                    sx={{
                      fontWeight: 500,
                      fontSize: "0.82rem",
                      bgcolor: isActive ? "rgba(255,255,255,0.15)" : "transparent",
                      borderRadius: 1,
                      "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
                    }}
                  >
                    {label}
                  </Button>
                );
              })}
              <Button color="inherit" component={RouterLink} to="/about" sx={{ fontWeight: 500 }}>
  About
</Button>
              <Button color="inherit" onClick={() => setContactDialogOpen(true)} sx={{ fontWeight: 500 }}>
                Contact
              </Button>

              {/* User section */}
              {user ? (
                <Box sx={{ display: "flex", alignItems: "center", ml: 2, gap: 1 }}>
                  {!rolesLoading && roles.length > 0 && (
                    <Chip label={roles[0]} size="small" color={getRoleColor(roles[0])}
                      sx={{ fontSize: "0.75rem", height: 24, fontWeight: 600 }} />
                  )}
                  <Box sx={{ mr: 1, display: { xs: "none", md: "block" }, position: "relative" }}>
                    <Typography variant="body2" sx={{ lineHeight: 1.3 }}>
                      {user?.name || user?.email}
                    </Typography>
                    {practiceInfo && (
                      practiceInfo.affiliatedPractices?.length > 1 ? (
                        <Box
                          onClick={() => setPracticeMenuOpen(!practiceMenuOpen)}
                          sx={{
                            display: "flex", alignItems: "center", gap: 0.25, cursor: "pointer",
                            "&:hover": { opacity: 0.8 },
                          }}
                        >
                          <Typography variant="caption" sx={{ opacity: 0.85, lineHeight: 1.2 }}>
                            {practiceInfo.organizationName}
                          </Typography>
                          <ArrowDropDownIcon sx={{ fontSize: 16, opacity: 0.85 }} />
                        </Box>
                      ) : (
                        <Typography variant="caption" sx={{ opacity: 0.85, lineHeight: 1.2, display: "block" }}>
                          {practiceInfo.organizationName}
                        </Typography>
                      )
                    )}
                    {practiceMenuOpen && (
                      <Box sx={{
                        position: "absolute", top: "100%", left: 0, mt: 0.5,
                        bgcolor: "background.paper", borderRadius: 2, boxShadow: 3,
                        minWidth: 220, zIndex: 1300, py: 1,
                      }}>
                        <Typography variant="caption" sx={{ px: 2, py: 0.5, display: "block", color: "text.secondary", fontWeight: 600 }}>
                          Switch Practice
                        </Typography>
                        {practiceInfo.affiliatedPractices.map((p) => (
                          <Box
                            key={p.organizationId}
                            onClick={() => !switchingPractice && !p.isActive && handleSwitchPractice(p.organizationId)}
                            sx={{
                              px: 2, py: 1, cursor: p.isActive ? "default" : "pointer",
                              display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1,
                              color: p.isActive ? "text.primary" : "text.secondary",
                              fontWeight: p.isActive ? 600 : 400,
                              "&:hover": { bgcolor: p.isActive ? "transparent" : "action.hover" },
                            }}
                          >
                            <Typography variant="body2" sx={{ fontWeight: "inherit" }}>{p.organizationName}</Typography>
                            {p.isActive && <Chip label="Current" size="small" sx={{ height: 18, fontSize: "0.65rem" }} />}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                  <IconButton onClick={() => setUserMenuOpen(!userMenuOpen)} size="large" sx={{ p: 0.5 }}>
                    <Avatar src={user?.picture} alt={user?.name} sx={{ width: 36, height: 36 }} />
                  </IconButton>
                  {userMenuOpen && (
                    <Box sx={{
                      position: "absolute", top: 64, right: 16,
                      bgcolor: "background.paper", borderRadius: 2,
                      boxShadow: 3, minWidth: 220, zIndex: 1300, py: 1,
                    }}>
                      <Box sx={{ px: 2, py: 1 }}>
                        <Typography variant="body2" fontWeight="bold">{user?.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{user?.email}</Typography>
                      </Box>
                      <Divider />
                      <ListItemButton onClick={() => { setUserMenuOpen(false); navigate("/dashboard"); }} sx={{ py: 1.5 }}>
                        <DashboardIcon sx={{ mr: 1.5, fontSize: 20, color: "text.secondary" }} />
                        <Typography variant="body2" color="text.primary">Dashboard</Typography>
                      </ListItemButton>
                      <ListItemButton onClick={() => { setUserMenuOpen(false); navigate("/profile"); }} sx={{ py: 1.5 }}>
                        <PersonIcon sx={{ mr: 1.5, fontSize: 20, color: "text.secondary" }} />
                        <Typography variant="body2" color="text.primary">My Profile</Typography>
                      </ListItemButton>
                      <ListItemButton onClick={() => { setUserMenuOpen(false); navigate("/profile", { state: { tab: 'account' } }); }} sx={{ py: 1.5 }}>
                        <SecurityIcon sx={{ mr: 1.5, fontSize: 20, color: "text.secondary" }} />
                        <Typography variant="body2" color="text.primary">Change Password</Typography>
                      </ListItemButton>
                      <Divider />
                      <ListItemButton onClick={handleLogout} sx={{ py: 1.5 }}>
                        <LogoutIcon sx={{ mr: 1.5, fontSize: 20, color: "#ff5252" }} />
                        <Typography variant="body2" color="#ff5252">Logout</Typography>
                      </ListItemButton>
                    </Box>
                  )}
                </Box>
              ) : (
                <Box sx={{ display: "flex", alignItems: "center", ml: 2, gap: 1.5 }}>
                  <Button color="inherit" startIcon={<PersonAddIcon />}
                    onClick={() => navigate("/signup")} sx={{ fontWeight: 500 }}>
                    Sign Up
                  </Button>
                  <Button variant="outlined" startIcon={<LoginIcon />}
                    onClick={() => isNative ? nativeLogin() : loginWithRedirect()}
                    sx={{ fontWeight: 500, color: "white", borderColor: "rgba(255,255,255,0.5)",
                      "&:hover": { borderColor: "white", bgcolor: "rgba(255,255,255,0.1)" } }}>
                    Login
                  </Button>
                   
                </Box>
              )}
            </>
          )}
        </Toolbar>
      </AppBar>

      {/* Side Panel */}
      <SidePanel
        open={activeMenu !== null}
        title={activeMenu}
        items={getActiveItems()}
        onClose={() => {
          setActiveMenu(null);
          if (typeof onPanelChange === "function") onPanelChange(false);
        }}
        onNavigate={handleNavigation}
        activeMenu={activeMenu}
      />

      {/* Mobile Drawer */}
      <Drawer variant="temporary" anchor="left" open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{ display: { xs: "block", lg: "none" },
          zIndex: (theme) => theme.zIndex.drawer + 2,
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: 280,
            // Explicitly above the AppBar's own zIndex (drawer + 1) —
            // on iOS/WebKit the AppBar's safe-area-inset-top padding can
            // create a stacking context that otherwise renders the fixed
            // header on top of the drawer's first ~60-80px, hiding the
            // drawer's own header and the Sign Up button beneath it.
            zIndex: (theme) => theme.zIndex.drawer + 2,
          } }}>
        {drawer}
      </Drawer>

      <ContactDialog open={contactDialogOpen} onClose={() => setContactDialogOpen(false)} />

      <Toolbar />
    </>
  );
}